import { Box, Typography } from "@mui/material";
import { motion, useReducedMotion } from "motion/react";
import { landingColors } from "../landingColors";
import { testimonials } from "../data/testimonialData";
import SectionContainer from "./SectionContainer";

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } },
};

// The design uses photography on three of the cards. We stand those in with
// saturated blues (white text) and tint the remaining cards, so the wall keeps
// the same rhythm without shipping stock portraits as if they were customers.
const cardTones = {
  light: {
    bg: "#EAF6FB",
    quote: landingColors.textPrimary,
    meta: landingColors.textSecondary,
  },
  mid: {
    bg: "#D6EDF7",
    quote: landingColors.textPrimary,
    meta: landingColors.textSecondary,
  },
  strong: {
    bg: "#BFE2F2",
    quote: landingColors.textPrimary,
    meta: landingColors.textSecondary,
  },
  deep: {
    bg: landingColors.accent,
    quote: "#FFFFFF",
    meta: "rgba(255,255,255,0.82)",
  },
  deepest: { bg: "#0A4A63", quote: "#FFFFFF", meta: "rgba(255,255,255,0.82)" },
};

const TestimonialWall = () => {
  const prefersReducedMotion = useReducedMotion();

  return (
    <SectionContainer>
      <Box sx={{ textAlign: "center", mb: { xs: 5, md: 7 } }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            color: landingColors.textPrimary,
            fontSize: { xs: 28, md: 40 },
          }}
        >
          What People Are Saying
        </Typography>
        <Typography
          sx={{
            color: landingColors.textSecondary,
            mt: 1.5,
            maxWidth: 560,
            mx: "auto",
          }}
        >
          A growing community of everyday people building trust and trading with
          confidence.
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          // The design's 3/6/3, 4/4/4, 6/6 spans only work once there's room
          // for three cards, so tablet drops to an even 2-up (every card spans
          // one column) rather than trying to preserve the feature widths.
          gridTemplateColumns: {
            xs: "repeat(1, 1fr)",
            sm: "repeat(2, 1fr)",
            md: "repeat(12, 1fr)",
          },
          gap: 2,
        }}
      >
        {testimonials.map((t, i) => {
          const tone = cardTones[t.tone] ?? cardTones.light;

          return (
            <Box
              key={t.name + i}
              component={motion.div}
              variants={itemVariants}
              initial={prefersReducedMotion ? "visible" : "hidden"}
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              sx={{
                gridColumn: { xs: "span 1", md: `span ${t.span}` },
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                minHeight: { xs: 0, md: 320 },
                bgcolor: tone.bg,
                borderRadius: 6,
                p: { xs: 3, md: 4 },
              }}
            >
              <Typography
                sx={{
                  color: tone.quote,
                  lineHeight: 1.6,
                  maxWidth: { md: t.span >= 6 ? 420 : "none" },
                }}
              >
                {`“${t.quote}”`}
              </Typography>

              <Box sx={{ mt: { xs: 3, md: 4 } }}>
                <Typography
                  sx={{
                    color: tone.quote,
                    fontWeight: 700,
                    fontStyle: "italic",
                  }}
                >
                  {t.name}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: tone.meta, fontStyle: "italic" }}
                >
                  {t.role}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </SectionContainer>
  );
};

export default TestimonialWall;
